import { useAuth } from "@/providers";
import {
  useSearchTablePreferences,
  useUpdateSearchTablePreferences,
} from "@/shared/domain/config/hooks";

import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_TABLE_COLUMNS,
  TableType,
} from "./tablePreferencesConfig";
import {
  useCreatePreferenceCallbacks,
  createPreferencesWithDefaults,
  useOrgPreferences,
} from "./tablePreferencesHelpers";

/**
 * Hook to manage topic table preferences (columns and rows per page).
 * Provides the current preferences and functions to update them.
 * Preferences are persisted to the backend via the config system.
 * Preferences are org-specific and fall back to defaults if not set for the current org.
 *
 * @returns Object containing preferences, save functions, loading states, and error handling
 */
export function useTopicTablePreferences() {
  const { currentOrganization } = useAuth();

  // Fetch only topic-specific config
  const configQuery = useSearchTablePreferences(
    "topic_search_table_preferences",
  );
  const updateMutation = useUpdateSearchTablePreferences(
    "topic_search_table_preferences",
  );

  // Extract org-scoped preferences
  const orgPreferences = useOrgPreferences(
    configQuery.data,
    currentOrganization?.org_id,
  );

  // Create preferences with defaults
  const preferences = createPreferencesWithDefaults(
    orgPreferences,
    DEFAULT_TABLE_COLUMNS[TableType.Topics],
    DEFAULT_ROWS_PER_PAGE,
  );

  // Create save callbacks
  const { saveColumns, saveRowsPerPage, clearError } =
    useCreatePreferenceCallbacks(updateMutation, currentOrganization?.org_id);

  return {
    preferences,
    saveColumns,
    saveRowsPerPage,
    isLoading: configQuery.isLoading,
    isSaving: updateMutation.isPending,
    error: updateMutation.error,
    clearError,
  };
}
